/**
 * cta-recipe.ts — the ONLY place the landing page's primary amber CTA
 * button's color/elevation/interaction/focus recipe resolves (R6 de-slop
 * round, judge-exact contract). Mirrors `ui/tone.ts` / `ui-light/light-
 * tokens.ts` / `constants/pricing-type.ts`'s discipline: every consumer
 * imports the pre-baked Tailwind-class STRING from here — never hand-types
 * the amber/shadow/focus recipe at a call site — so there is exactly one
 * place the primary CTA's look can drift.
 *
 * SCOPE (deliberately narrow): this module owns COLOR, ELEVATION (the amber
 * shadow), INTERACTION (hover/active background swaps), and FOCUS (the
 * outline ring) ONLY. Call sites own geometry (padding, width, height) and
 * typography (size, weight, tracking) — exactly the same split
 * `pricing-type.ts` draws between itself (typography only) and its call
 * sites (color composes separately, e.g. `cn(PRICING_TYPE.row,
 * 'text-[#949BA6]')`). Compose at the call site:
 * `cn(LANDING_PRIMARY_CTA_RECIPE, 'h-12 px-6 rounded-[10px] text-[15px] font-semibold')`.
 *
 * FOCUS-FADE-IN FIX (the class `check:landing-cta-recipe` kills): focus is
 * INSTANT (no transition delay) because `outline-color` is deliberately NOT
 * in the `transition-[...]` property list below — only `background-color`
 * and `box-shadow` are. A keyboard user tabbing to the button must see the
 * focus ring appear immediately, not fade in over the hover transition's
 * duration. Call sites MUST NOT add a second `transition-property` utility
 * (e.g. `transition-all` or another `transition-[...]`) alongside this
 * recipe — two `transition-property` declarations on the same element is
 * CSS specificity/order roulette (whichever utility's class name sorts
 * later in the generated stylesheet wins, not whichever the author intended)
 * and could silently re-delay the outline this recipe exists to keep
 * instant.
 *
 * HOVER-GRAMMAR BOUNDARY: `ui/hover-grammar.ts` exists and governs a
 * DIFFERENT primitive's interaction motion — `Surface`'s card hover-promote/
 * lift (`HOVER_SURFACE`/`HOVER_LIFT`, consumed via `Surface`'s `hover` prop).
 * It does not govern CTA button hover today: this recipe's `hover:bg-
 * [#FBBF24]` / `active:bg-[#D97706]` swaps are Chip/button-specific and are
 * NOT routed through `hover-grammar.ts`'s exports. This file does not change
 * `hover-grammar.ts`, and `hover-grammar.ts` is not touched to accommodate
 * this recipe — the two interaction systems stay independent unless a future
 * pass deliberately unifies them.
 *
 * Related: [[rules/enforcements]] #49 · `ui/tone.ts` (sibling registry) ·
 * `ui/dark-tokens.ts` (sibling registry, same round) ·
 * `scripts/check-landing-cta-recipe.mjs` (enforcer: bans the raw recipe
 * literal outside this file, and bans the transition+box-shadow-ring
 * combination this recipe's instant-focus design replaces).
 */
export const LANDING_PRIMARY_CTA_RECIPE = [
  'bg-[#F59E0B] text-[#090909]',
  'shadow-[0_8px_24px_rgba(245,158,11,0.18)]',
  'hover:bg-[#FBBF24]',
  'hover:shadow-[0_8px_24px_rgba(245,158,11,0.24)]',
  'active:bg-[#D97706]',
  'transition-[background-color,box-shadow]',
  'duration-[var(--motion-fast)] ease-[var(--ease-ui)]',
  'motion-reduce:transition-none',
  'outline outline-2 outline-transparent outline-offset-2',
  'focus-visible:outline-[#FBBF24]',
].join(' ');
